// src/pages/imamMahdi/UltimateReformer.jsx
import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const UltimateReformer = () => {
  return (
    <>
      <Helmet>
        <title>Imam Mahdi (AJ) – His Role as the Ultimate Reformer | Ziyarat Guide</title>
        <meta
          name="description"
          content="Discover the role of Imam Mahdi (AJ) as the ultimate reformer who will fill the world with justice after tyranny. Learn about his mission, reform, and global impact."
        />
        <meta property="og:title" content="Imam Mahdi (AJ) – His Role as the Ultimate Reformer" />
        <meta
          property="og:description"
          content="Explore how Imam Mahdi (AJ) will establish global justice, reform humanity, and renew spiritual values according to Islamic teachings."
        />
        <meta
          property="og:image"
          content="https://ziyaratguide.com/wp-content/uploads/2025/08/third-His-role-as-the-ultimate-reformer-.png"
        />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="imam-page">
        <div className="imam-content">
          <h1 className="page-title">His Role as the Ultimate Reformer</h1>

          <div className="content-section">
            <img
              src="https://ziyaratguide.com/wp-content/uploads/2025/08/third-His-role-as-the-ultimate-reformer-.png"
              alt="Ultimate Reformer"
              className="imam-image"
            />

            <h2>How He Will Fill the World with Justice After It Is Full of Tyranny</h2>
            <p>
              Across the Qur’an and countless sayings of the Prophet Muhammad (peace be upon him and his family), there runs a central theme: no matter how widespread oppression and corruption become, God will ultimately establish justice on earth.
            </p>
            <p>
              In Shia Islam, this promise is embodied in the awaited role of Imam Mahdi (peace be upon him) — the twelfth Imam, who will appear by Allah’s command to reform the world.
            </p>

            <h2>Filling the World with Justice</h2>
            <p>
              The Prophet (pbuh) said, in widely narrated hadith (found in both Sunni and Shia sources):
            </p>
            <blockquote>
              “If there were only one day left of the life of this world, Allah would extend it until He sends a man from my family, whose name is like my name and whose father’s name is like my father’s name. He will fill the earth with equity and justice just as it was filled with oppression and tyranny.”
            </blockquote>

            <p>This is the core of Imam Mahdi’s mission:</p>
            <ul>
              <li>To end global injustice, corruption and tyranny.</li>
              <li>To raise humanity’s spiritual and moral state.</li>
              <li>To establish a society founded on truth, compassion and divine law.</li>
            </ul>

            <h2>A World Yearning for Change</h2>
            <p>
              Shia narrations describe that before his appearance, the world will be steeped in injustice, where many suffer under the weight of oppression and corruption. People will yearn for a saviour — not just materially, but spiritually.
            </p>
            <p>
              Imam Mahdi will reform hearts and societies. Under his leadership:
            </p>
            <ul>
              <li>The weak and downtrodden will be uplifted.</li>
              <li>The arrogant and tyrannical will lose their power.</li>
              <li>Knowledge will flourish, and humanity will reach a level of fairness and prosperity never seen before.</li>
            </ul>

            <h2>Reform Beyond Politics</h2>
            <p>
              His reform is not merely political. Imam Mahdi’s mission is to renew the hearts of humanity, guiding people to live with sincerity, honesty, and love of God.
            </p>
            <ul>
              <li>Justice will become so universal that even the earth will yield its treasures, and no one will be left needy.</li>
              <li>People will achieve a spiritual closeness to Allah unlike any previous generation.</li>
              <li>Conflicts and grudges will dissolve, replaced by brotherhood and compassion.</li>
            </ul>

            <h2>In Daily Life</h2>
            <p>
              This is why, for Shia Muslims, awaiting Imam Mahdi is not passive. It means striving to:
            </p>
            <ul>
              <li>Live truthfully and compassionately today.</li>
              <li>Stand against any form of oppression or injustice.</li>
              <li>Prepare oneself spiritually, so as to be worthy of serving under his banner when he appears.</li>
            </ul>
            <p>
              Imam Mahdi (aj) is the ultimate reformer appointed by Allah, who will appear to fill the world with justice, compassion and truth after it has been drenched in tyranny. Waiting for him is not simply hope for a future — it is a daily commitment to live by the very values he will establish.
            </p>
          </div>
        </div>

        {/* Sidebar Navigation */}
        <aside className="imam-sidebar">
          <h2>Explore More</h2>
          <ul>
            <li><Link to="/imam-mahdi">Who is Imam Mahdi?</Link></li>
            <li><Link to="/imam-mahdi/described-in-quran-hadith">Described In Quran & Hadith</Link></li>
            <li><Link to="/imam-mahdi/how-to-talk">How To Talk</Link></li>
            <li><Link to="/imam-mahdi/impact-appearance">Impact of His Appearance</Link></li>
            <li><Link to="/imam-mahdi/misconceptions">Misconceptions</Link></li>
            <li><Link to="/imam-mahdi/practices-allegiance">Practices & Allegiance</Link></li>
            <li><Link to="/imam-mahdi/reflections-stories">Reflections & Stories</Link></li>
            <li><Link to="/imam-mahdi/return">Return</Link></li>
            <li><Link to="/imam-mahdi/signs-reappearance">Signs of Reappearance</Link></li>
            <li><Link to="/imam-mahdi/supporters">Supporters</Link></li>
            <li><Link to="/imam-mahdi/ultimate-reformer">The Ultimate Reformer</Link></li>
            <li><Link to="/imam-mahdi/why-occultation">Why Occultation</Link></li>
          </ul>
        </aside>
      </div>
    </>
  );
};

export default UltimateReformer;
